import { Box, Divider, Grid, styled, Tab, TabProps, Tabs } from '@mui/material'
import { SetStateAction, SyntheticEvent, useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { ContentWrapper, TabPanel } from '../..'
import { useIsMobile } from '../../../contexts'
import { isArrayWithLength } from '../../../utils'

const tabBorder = '1px solid #CCCCCC'

const StyledTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    backgroundColor: 'transparent'
  },
  borderTop: tabBorder,
  margin: 0
})

interface StyledTabProps extends TabProps {
  isMobile: boolean
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StyledTab = styled(({ isMobile, ...props }: StyledTabProps) => (
  <Tab {...props} />
))(({ theme, isMobile }) => {
  const paddingBottomAndTop = isMobile ? '40px' : '15px'
  const tabStyles = {
    borderBottom: tabBorder,
    padding: `${paddingBottomAndTop} 15px ${paddingBottomAndTop}`,
    marginRight: 0
  }
  return {
    ...tabStyles,
    alignItems: 'flex-start',
    '&:hover': tabStyles,
    '&.Mui-selected': {
      ...tabStyles,
      borderLeft: `5px solid ${theme.variables.colors.tabNavigation.tabLinkActive.color}`,
      backgroundColor: '#F9F9F9'
    },
    '&.Mui-focusVisible': tabStyles,
    '&:hover.Mui-selected': tabStyles
  }
})

interface TabElementProps<T> {
  item: T
}

type TabElementType<T> = ({ item }: TabElementProps<T>) => JSX.Element

type TabParams = {
  id?: string
}

export interface VerticalTabPanelProps<T> {
  tabItems: T[]
  TabContentElement: TabElementType<T>
  TabNavTitleElement: TabElementType<T>
}

export const VerticalTabPanel = <T,>({
  tabItems,
  TabContentElement,
  TabNavTitleElement
}: VerticalTabPanelProps<T>) => {
  const params: TabParams = useParams()

  const [selectedTab, setSelectedTab] = useState(0)

  useEffect(() => {
    const id = params?.id
    const routedTab = id ? parseInt(id) : 0
    if (routedTab) {
      setSelectedTab(routedTab)
    }
  }, [params?.id])

  const isMobile = useIsMobile()

  const handleChange = (
    _event: SyntheticEvent<Element, Event>,
    newTab: SetStateAction<number>
  ) => {
    setSelectedTab(newTab)
  }

  return (
    <ContentWrapper showContent={isArrayWithLength(tabItems)}>
      <Grid container pt={1}>
        <Grid item md={3} sm={12} xs={12}>
          <StyledTabs
            aria-label="Vertical tabs"
            onChange={handleChange}
            orientation="vertical"
            value={selectedTab}
            variant={isMobile ? 'fullWidth' : 'standard'}
          >
            {tabItems.map((item: T, index: number) => {
              return (
                <StyledTab
                  aria-controls={`vertical-tabpanel-${index}`}
                  id={`vertical-tab-${index}`}
                  isMobile={isMobile}
                  key={'vertical-tab-' + index}
                  label={<TabNavTitleElement item={item} />}
                />
              )
            })}
          </StyledTabs>
        </Grid>
        <Grid item md={true} sm={12} xs={12}>
          {tabItems.map((item: T, index: number) => (
            <TabPanel
              key={`vertical-tabpanel-${index}`}
              index={index}
              prefix="vertical"
              selectedTab={selectedTab}
            >
              <Box sx={{ pl: isMobile ? 0 : 10, pt: isMobile ? 2 : 0 }}>
                <TabContentElement item={item} />
              </Box>
            </TabPanel>
          ))}
        </Grid>
        <Divider />
      </Grid>
    </ContentWrapper>
  )
}
